/**
 * Course List Component
 * Task 51: Course catalog for education institutions
 */

import { useState, useMemo } from 'react';
import { IndustryCourse } from '@/types/industry';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { MoreHorizontal, Pencil, Trash2, Plus, Search, GraduationCap, Users, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CourseListProps {
  courses: IndustryCourse[];
  onEdit?: (course: IndustryCourse) => void;
  onDelete?: (course: IndustryCourse) => void;
  onCreate?: () => void;
}

type StatusFilter = 'all' | 'active' | 'inactive';

export function CourseList({ courses, onEdit, onDelete, onCreate }: CourseListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  // Extract unique categories (subject areas)
  const categories = useMemo(() => {
    const cats = new Set<string>();
    courses.forEach((c) => {
      const category = c.metadata?.category as string;
      if (category) cats.add(category);
    });
    return Array.from(cats).sort();
  }, [courses]);

  // Filter courses
  const filteredCourses = useMemo(() => {
    return courses.filter((course) => {
      // Search filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const matchesName = course.name?.toLowerCase().includes(term);
        const matchesCategory = course.metadata?.category?.toString().toLowerCase().includes(term);
        if (!matchesName && !matchesCategory) return false;
      }

      // Status filter
      if (statusFilter !== 'all') {
        const isActive =
          course.is_active ?? ['published', 'in_progress'].includes(course.status ?? 'draft');
        if (statusFilter === 'active' && !isActive) return false;
        if (statusFilter === 'inactive' && isActive) return false;
      }

      // Category filter
      if (categoryFilter !== 'all') {
        const category = course.metadata?.category as string;
        if (category !== categoryFilter) return false;
      }

      return true;
    });
  }, [courses, searchTerm, statusFilter, categoryFilter]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDuration = (minutes?: number) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}min`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}min`;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="h-5 w-5" />
                Cursos
              </CardTitle>
              <CardDescription>Gerencie o catálogo de cursos</CardDescription>
            </div>
            {onCreate && (
              <Button onClick={onCreate}>
                <Plus className="mr-2 h-4 w-4" />
                Novo Curso
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar cursos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={(value: StatusFilter) => setStatusFilter(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Status</SelectItem>
                <SelectItem value="active">Ativos</SelectItem>
                <SelectItem value="inactive">Inativos</SelectItem>
              </SelectContent>
            </Select>

            {/* Category Filter */}
            {categories.length > 0 && (
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as Áreas</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Results Count */}
          <div className="mt-4 text-sm text-muted-foreground">
            Mostrando {filteredCourses.length} de {courses.length} cursos
          </div>
        </CardContent>
      </Card>

      {/* Courses Table */}
      <Card>
        <CardContent className="pt-6">
          {filteredCourses.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              {searchTerm || statusFilter !== 'all' || categoryFilter !== 'all'
                ? 'Nenhum curso encontrado com os filtros selecionados'
                : 'Nenhum curso cadastrado'}
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Curso</TableHead>
                    <TableHead>Área</TableHead>
                    <TableHead>Duração</TableHead>
                    <TableHead>Vagas</TableHead>
                    <TableHead>Valor</TableHead>
                    <TableHead>Parcelas</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCourses.map((course) => {
                    const category = course.metadata?.category as string;
                    const rawDuration = course.metadata?.duration as number | undefined;
                    const duration =
                      typeof rawDuration === 'number'
                        ? rawDuration
                        : typeof course.duration_hours === 'number'
                          ? course.duration_hours * 60
                          : undefined;
                    const rawCapacity = course.metadata?.capacity as number | undefined;
                    const capacity =
                      typeof course.max_students === 'number' ? course.max_students : rawCapacity;
                    const rawInstallments = course.metadata?.installments as number | undefined;
                    const installments =
                      typeof course.max_installments === 'number'
                        ? course.max_installments
                        : rawInstallments;
                    const isActive =
                      course.is_active ??
                      ['published', 'in_progress'].includes(course.status ?? 'draft');

                    return (
                      <TableRow key={course.id}>
                        <TableCell>
                          <div className="font-medium">{course.name}</div>
                          {course.metadata?.schedule && (
                            <div className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                              <Clock className="h-3 w-3" />
                              {course.metadata.schedule as string}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {category ? (
                            <Badge variant="outline">{category}</Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{formatDuration(duration)}</TableCell>
                        <TableCell>
                          {capacity ? (
                            <div className="flex items-center gap-1">
                              <Users className="h-4 w-4 text-muted-foreground" />
                              <span>{capacity}</span>
                            </div>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell className="font-medium">
                          {course.price ? formatCurrency(course.price) : '-'}
                        </TableCell>
                        <TableCell>
                          {installments ? `${installments}x` : 'À vista'}
                        </TableCell>
                        <TableCell>
                          <Badge variant={isActive ? 'default' : 'secondary'}>
                            {isActive ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {onEdit && (
                                <DropdownMenuItem onClick={() => onEdit(course)}>
                                  <Pencil className="mr-2 h-4 w-4" />
                                  Editar
                                </DropdownMenuItem>
                              )}
                              {onDelete && (
                                <DropdownMenuItem
                                  onClick={() => onDelete(course)}
                                  className="text-destructive"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Excluir
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
